import { useState } from "react";
import { useLocation } from "wouter";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Archive, ExternalLink, Mail, Calendar, Info, User, MapPin } from "lucide-react";
import { format } from "date-fns";
import { PlayerMessage } from "@/lib/types";

interface MessageDetailModalProps {
  message: PlayerMessage;
  isOpen: boolean;
  onClose: () => void;
  onArchive: (messageId: number) => void;
}

export default function MessageDetailModal({
  message,
  isOpen,
  onClose,
  onArchive,
}: MessageDetailModalProps) {
  const [, setLocation] = useLocation();

  const handleViewPlayer = () => {
    if (message.player_id) {
      // Navigate to player database with the player selected
      // You might need to implement a way to select/highlight the player
      setLocation(`/players?playerId=${message.player_id}`);
      onClose();
    }
  };

  const handleArchive = () => {
    onArchive(message.id);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <div className="flex items-start justify-between">
            <div>
              <DialogTitle className="text-2xl flex items-center gap-2">
                {message.players?.name || "Unknown Player"}
                {!message.is_read && (
                  <Badge variant="default">New</Badge>
                )}
              </DialogTitle>
              <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  {format(new Date(message.email_date), "MMMM d, yyyy 'at' h:mm a")}
                </div>
                {message.players?.email && (
                  <div className="flex items-center gap-1">
                    <Mail className="h-4 w-4" />
                    {message.players.email}
                  </div>
                )}
              </div>
            </div>
          </div>
        </DialogHeader>

        <ScrollArea className="flex-1 max-h-[calc(90vh-200px)]">
          <div className="space-y-6 p-1">
            {/* Player Info Card */}
            {message.players && (
              <Card>
                <CardContent className="p-4">
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Player Information
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                    {message.players.position && (
                      <div>
                        <span className="text-muted-foreground">Position:</span>
                        <p className="font-medium">{message.players.position.join(", ")}</p>
                      </div>
                    )}
                    {message.players.current_club && (
                      <div>
                        <span className="text-muted-foreground">Current Club:</span>
                        <p className="font-medium">{message.players.current_club}</p>
                      </div>
                    )}
                    {message.players.nationality && (
                      <div>
                        <span className="text-muted-foreground">Nationality:</span>
                        <p className="font-medium">{message.players.nationality}</p>
                      </div>
                    )}
                    {message.players.age && (
                      <div>
                        <span className="text-muted-foreground">Age:</span>
                        <p className="font-medium">{message.players.age}</p>
                      </div>
                    )}
                    {message.players.height && (
                      <div>
                        <span className="text-muted-foreground">Height:</span>
                        <p className="font-medium">{message.players.height}</p>
                      </div>
                    )}
                    {message.players.weight && (
                      <div>
                        <span className="text-muted-foreground">Weight:</span>
                        <p className="font-medium">{message.players.weight}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* New Information */}
            {message.has_new_info && message.new_info_extracted && (
              <Card className="border-blue-200 bg-blue-50/50">
                <CardContent className="p-4">
                  <h3 className="font-semibold mb-3 flex items-center gap-2 text-blue-900">
                    <Info className="h-4 w-4" />
                    New Information in This Message
                  </h3>
                  <div className="space-y-2">
                    {Object.entries(message.new_info_extracted).map(([key, value]) => (
                      <div key={key} className="text-sm">
                        <span className="font-medium text-blue-800 capitalize">
                          {key.replace(/_/g, " ")}:
                        </span>{" "}
                        <span className="text-blue-700">
                          {Array.isArray(value) ? value.join(", ") : String(value)}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Email Content */}
            <div>
              <h3 className="font-semibold mb-3">Email Subject</h3>
              <p className="text-sm mb-4">{message.subject}</p>

              <Separator className="my-4" />

              <h3 className="font-semibold mb-3">Email Content</h3>
              <div className="bg-muted/50 rounded-lg p-4">
                <p className="text-sm whitespace-pre-wrap">
                  {message.full_body || message.body_preview}
                </p>
              </div>
            </div>
          </div>
        </ScrollArea>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button variant="outline" onClick={handleArchive}>
            <Archive className="h-4 w-4 mr-2" />
            Archive Message
          </Button>
          <Button onClick={handleViewPlayer}>
            <ExternalLink className="h-4 w-4 mr-2" />
            View Player Profile
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}